<template>
    <hox-modal :show-footer="canDuplicateCampaign" @close="close">
        <hox-loading-layer v-if="isLoadingDuplicateCampaign || isLoadingCampaign" :is-full-screen="false" />

        <template #header>Duplicate campaign</template>

        <hox-alert v-if="hasErrorLoadingCampaign" margin-bottom="none" type="danger">
            <template #title>Uh oh!</template>
            <template #content>
                <p>Something went wrong while getting some data. Try again in a few moments.</p>
            </template>
            <template #actionItems>
                <Button type="primary" @click="refetchCampaign">Retry</Button>
            </template>
        </hox-alert>

        <hox-alert v-else-if="masterTemplateOptions.length === 0" margin-top="base" type="warning">
            <template #content>
                <p>The campaign you have selected cannot be duplicated as it has no master templates.</p>
            </template>
        </hox-alert>

        <template v-else>
            <CampaignForm
                v-if="initialFormData"
                ref="form"
                :disable-wpp-project-id="isWppLogin"
                disable-campaign-type
                disable-campaign-type-reason="Template type is inherited from the campaign being duplicated"
                :has-platform-metadata="isCopyingPlatformMetadata"
                oms-channel-disabled-reason="Channel cannot be changed while the platform metadata is being copied"
                fit-content
                :initial-form-data="initialFormData"
                :sections="sections"
                :has-creative-x-enabled="hasCreativeXEnabled"
                :client-creative-x-secret-id="clientCreativeXSecretId"
                @hasValidationErrorsUpdate="hasFormValidationErrors = $event"
                @submit="duplicateCampaign"
            />

            <div class="checkbox-group-wrapper">
                <label>Master Templates</label>

                <CheckboxGroup v-model="selectedMasterTemplates">
                    <Checkbox v-for="option in masterTemplateOptions" :key="option.value" :label="option.value">
                        {{ option.label }}
                    </Checkbox>
                </CheckboxGroup>

                <p v-for="error in validationErrors.selectedMasterTemplates" :key="error" class="error-wrapper">
                    {{ error }}
                </p>
            </div>

            <div class="checkbox-group-wrapper">
                <label>Languages</label>

                <CheckboxGroup v-model="selectedLanguages">
                    <Checkbox
                        v-for="option in languageOptions"
                        :key="option.value"
                        :label="option.value"
                        :disabled="option.value === 'en'"
                    >
                        {{ option.label }}
                        <span v-if="option.value === 'en'">(Required)</span>
                    </Checkbox>
                </CheckboxGroup>

                <p v-for="error in validationErrors.selectedLanguages" :key="error" class="error-wrapper">
                    {{ error }}
                </p>
            </div>

            <div class="checkbox-group-wrapper">
                <label>Also copy</label>

                <CheckboxGroup v-model="selectedExtras">
                    <Checkbox
                        v-for="option in extraOptions"
                        :key="option.value"
                        :label="option.value"
                        :data-testid="`duplicate-campaign-modal__checkbox--${option.value}`"
                    >
                        {{ option.label }}
                    </Checkbox>
                </CheckboxGroup>
            </div>

            <hox-alert margin-bottom="none" margin-top="large" type="info">
                <template #content>
                    <p>
                        Duplicating a campaign will create a new campaign with the selected master templates and
                        languages, along with all creative content belonging to them.
                    </p>

                    <p>Team members and the ad server publish state will not be brought over to the new campaign.</p>

                    <p>
                        Duplicating runs in the background. You can close this and follow its progress in your
                        notifications.
                    </p>
                </template>
            </hox-alert>

            <p v-for="error in mutationErrors" :key="error" class="error-wrapper">Error: {{ error }}</p>
        </template>

        <template #footer>
            <Button
                type="primary"
                :disabled="isLoadingDuplicateCampaign || hasValidationErrors"
                data-testid="duplicate-campaign-modal__button--duplicate"
                @click="submit"
            >
                Duplicate
            </Button>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { ApolloError } from "@apollo/client/core";

import CampaignForm, { type CampaignFormData } from "@/components/CampaignForm.vue";

import campaignWithLanguagesAndMasterTemplatesQuery from "@/apollo/queries/v2/CampaignWithLanguagesAndMasterTemplates.gql";
import copyCampaignMutation from "@/apollo/mutations/v2/CopyCampaign.gql";

import { generateMasterTemplateFilterOptions } from "@/utils";
import { generateJobId, waitForJobToExist } from "@/utils/jobs";

import type { CampaignSection, EditableGroup } from "@/types/types";

import { AuthGetters } from "@/store/modules/auth";
import { JobsAction } from "@/store/modules/jobs";
import { NotificationsAction } from "@/store/modules/notifications";
import { JobNames, JobStatus } from "@/enums/jobs";
import { NotificationStatus, NotificationTypes } from "@/enums/notifications";
import { PlatformTypeOption } from "@/enums/platforms";
import getDefaultCreativeXCampaignSettings from "@/utils/CreativeX/getDefaultCreativeXCampaignSettings";
import { localeToFriendlyName, type LanguageCode } from "@/utils/languages";

/*
 * Everything here is off by default so that duplicating stays a clean copy unless it is asked for. The
 * OMS platform metadata is the exception: not copying it is a bug rather than a choice, so it is ticked
 * whenever the source campaign has any.
 */
const CopyOption = {
    Annotations: "annotations",
    ReviewStatus: "reviewStatus",
    ProductionStatus: "productionStatus",
    CustomColumns: "customColumns",
    PlatformMetadata: "platformMetadata"
} as const;

// the checkboxes, in the order they are offered
const COPY_OPTIONS = [
    { value: CopyOption.Annotations, label: "Comments and annotations" },
    { value: CopyOption.ReviewStatus, label: "Review status" },
    { value: CopyOption.ProductionStatus, label: "Production status" },
    { value: CopyOption.CustomColumns, label: "Custom columns and values" },
    { value: CopyOption.PlatformMetadata, label: "CO platform metadata" }
];

/*
 * How long to keep looking for the job before deciding the copy never started. Generous because the
 * backend validates the whole request before it creates the job document.
 */
const JOB_APPEARED_ATTEMPTS = 20;
const JOB_APPEARED_WAIT_MS = 500;

export default defineComponent({
    components: {
        CampaignForm
    },

    props: {
        campaignId: {
            required: true,
            type: String
        },

        sections: {
            required: true,
            type: Array as PropType<CampaignSection[]>
        },

        hasCreativeXEnabled: {
            type: Boolean,
            default: false
        },

        hasConnectEnabled: {
            type: Boolean,
            default: false
        },

        clientCreativeXSecretId: {
            type: String,
            required: false
        }
    },

    emits: ["close", "campaignDuplicated"],

    data() {
        return {
            initialFormData: null as Partial<CampaignFormData> | null,
            hasErrorLoadingCampaign: false,
            hasFormValidationErrors: false,
            isLoadingCampaign: 0,
            isLoadingDuplicateCampaign: false,
            languageOptions: [] as { label: string; value: string }[],
            masterTemplateOptions: [] as { width: number; height: number; value: string; label: string }[],
            selectedLanguages: [] as string[],
            selectedMasterTemplates: [] as string[],
            selectedExtras: [] as string[],
            hasPlatformMetadata: false,
            mutationErrors: [] as string[],
            isWppLogin: false
        };
    },

    computed: {
        canDuplicateCampaign() {
            return this.masterTemplateOptions.length > 0;
        },

        extraOptions() {
            // there is nothing to copy, so nothing to offer, unless the source campaign has OMS metadata
            return COPY_OPTIONS.filter(
                ({ value }) => value !== CopyOption.PlatformMetadata || this.hasPlatformMetadata
            );
        },

        copyOptions() {
            return Object.fromEntries(
                Object.values(CopyOption).map(option => [option, this.selectedExtras.includes(option)])
            );
        },

        /*
         * The publisher editables are chosen from the OMS channel, so the channel has to stay put while
         * they are being copied - otherwise the new campaign would get, say, Meta fields on a TikTok
         * channel. CampaignForm disables the channel select on this.
         */
        isCopyingPlatformMetadata() {
            return this.hasPlatformMetadata && this.selectedExtras.includes(CopyOption.PlatformMetadata);
        },

        hasValidationErrors() {
            return this.hasFormValidationErrors || Object.keys(this.validationErrors).length > 0;
        },

        validationErrors() {
            const errors = {} as Record<string, string[]>;

            if (this.selectedLanguages.length === 0) {
                errors.selectedLanguages = ["At least 1 language needs to be selected."];
            }

            if (this.selectedMasterTemplates.length === 0) {
                errors.selectedMasterTemplates = ["At least 1 master template needs to be selected."];
            }

            return errors;
        }
    },

    mounted() {
        this.isWppLogin = this.$store.getters[AuthGetters.isWppLogin];
    },

    methods: {
        close() {
            this.$emit("close");
        },

        refetchCampaign() {
            this.$apollo.queries.campaign.refetch();
        },

        submit() {
            (this.$refs.form as HTMLFormElement).submit();
        },

        async duplicateCampaign(formData: CampaignFormData) {
            if (this.hasValidationErrors || this.isLoadingDuplicateCampaign) {
                return;
            }

            this.isLoadingDuplicateCampaign = true;
            this.mutationErrors = [];

            /*
             * The copy runs inline in the API lambda, which takes far longer than the gateway will wait
             * for a response, so this mutation normally times out long after the work has actually
             * started. Generating the job id here is what makes that survivable: the notification is
             * registered against an id we already know and tracks the copy through the job document,
             * whatever happens to the request.
             */
            const jobId = generateJobId();
            const variables = {
                campaignId: this.campaignId,
                languages: this.selectedLanguages,
                masterTemplateIds: this.selectedMasterTemplates,
                copyOptions: this.copyOptions,
                jobId,

                name: formData.name,
                sectionId: formData.sectionId === "none" ? null : formData.sectionId,
                ...(this.hasConnectEnabled && {
                    opendcPlatformSettings: formData.opendcPlatformSettings
                })
            };

            /*
             * Settled into a value rather than awaited, so that we can wait on the job appearing instead
             * of on the response. The catch also keeps the eventual rejection from being reported as an
             * unhandled one.
             */
            const mutationOutcome = this.$apollo
                .mutate({ mutation: copyCampaignMutation, variables })
                .then(() => null)
                .catch((error: unknown) => error);

            if (!(await this.waitForDuplicationToStart(jobId, mutationOutcome))) {
                const graphQLErrors = ((await mutationOutcome) as ApolloError)?.graphQLErrors || [];
                this.mutationErrors = graphQLErrors.length
                    ? graphQLErrors.map(({ message }) => message)
                    : ["Something went wrong while duplicating the campaign. Try again in a few moments."];
                this.isLoadingDuplicateCampaign = false;
                return;
            }

            /*
             * From here the job document is the source of truth. The backend always drives it to a
             * terminal state, so the notification drawer's poll reports the outcome and there is nothing
             * left for this component to do.
             */
            await this.registerDuplicationNotification(jobId);
        },

        /**
         * The backend validates its input and only then creates the job document, so the job turning up is
         * the signal that the copy has really started. Waiting for it means input errors such as a
         * duplicate name are still shown inline instead of closing the modal and losing the selections.
         *
         * Whichever settles first decides: the job appearing, or the mutation coming back. A mutation that
         * resolves outright means the copy finished quickly enough to answer, so that counts as started too.
         */
        async waitForDuplicationToStart(jobId: string, mutationOutcome: Promise<unknown>) {
            const hasStarted = await Promise.race([
                waitForJobToExist(this.$apollo, jobId, JOB_APPEARED_ATTEMPTS, JOB_APPEARED_WAIT_MS),
                mutationOutcome.then(error => !error)
            ]);

            if (hasStarted) {
                return true;
            }

            // The mutation failed, but that may only have been the connection dropping, so give the job one
            // last chance to show up before blaming the user's input.
            return waitForJobToExist(this.$apollo, jobId, 3, JOB_APPEARED_WAIT_MS);
        },

        /**
         * Registers a notification against the running job, then closes the modal. The drawer's poll
         * replaces this placeholder with the real job document on its next tick.
         */
        async registerDuplicationNotification(jobId: string) {
            const job = {
                _id: jobId,
                name: JobNames.CopyCampaign,
                status: JobStatus.PENDING,
                message: "Pending",
                progress: 0,
                payload: { campaignId: this.campaignId }
            };

            const notificationId = await this.$store.dispatch(NotificationsAction.Add, {
                message: job.message,
                status: NotificationStatus.Pending,
                type: NotificationTypes.CopyCampaign,
                job
            });

            this.$store.dispatch(JobsAction.SetNotificationIdByActiveJobId, { jobId, notificationId });
            this.$snackbar.info("Duplicating campaign. Take a look at your notifications to check on progress");
            this.$emit("campaignDuplicated");
            this.close();
        }
    },

    apollo: {
        campaign: {
            query: campaignWithLanguagesAndMasterTemplatesQuery,
            fetchPolicy: "network-only",
            loadingKey: "isLoadingCampaign",
            variables() {
                return {
                    campaignId: this.campaignId
                };
            },
            result({ data: { campaign, editableGroupsByCampaign }, error }) {
                if (error) {
                    this.hasErrorLoadingCampaign = true;
                    return;
                }

                this.hasErrorLoadingCampaign = false;

                this.hasPlatformMetadata = !!editableGroupsByCampaign?.find(
                    (group: EditableGroup) =>
                        PlatformTypeOption.facebookPlatform === group.name ||
                        PlatformTypeOption.platformMetadata === group.name
                );

                if (this.hasPlatformMetadata) {
                    this.selectedExtras = [...this.selectedExtras, CopyOption.PlatformMetadata];
                }

                this.initialFormData = {
                    name: `Copy of ${campaign.name}`,
                    // always inherited: the backend copies the source campaign's template type regardless
                    campaignType: campaign.campaignType,
                    jiraTicketUrl: campaign.jiraTicketUrl,
                    sectionId: campaign.section?._id,
                    toneOfVoice: campaign.toneOfVoice,
                    defaultTimelineLength: campaign.defaultTimelineLength,
                    wppProjectId: campaign.wppProjectId,
                    ...(this.hasCreativeXEnabled && {
                        creativeXPlatformSettings: {
                            ...getDefaultCreativeXCampaignSettings(),
                            ...campaign.creativeXPlatformSettings
                        }
                    }),
                    ...(this.hasConnectEnabled && {
                        opendcPlatformSettings: campaign.opendcPlatformSettings
                    })
                };

                this.languageOptions = campaign.languages.map((language: LanguageCode) => ({
                    label: localeToFriendlyName(language),
                    value: language
                }));

                this.selectedLanguages = [...campaign.languages];
                this.masterTemplateOptions = generateMasterTemplateFilterOptions(campaign.masterTemplates);
                this.selectedMasterTemplates = this.masterTemplateOptions.map(({ value }) => value);
            }
        }
    }
});
</script>

<style scoped lang="css">
.checkbox-group-wrapper {
    margin-top: 12px;

    .ivu-checkbox-group-item {
        display: block;
    }
}

.error-wrapper {
    color: red;
    margin-top: 12px;
}
</style>
